"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"
import { getQueryTenantId } from "@/lib/auth/query-tenant-id"
import { metaAdsIntegrationQueryKey } from "@/hooks/queries/meta-ads"
import type {
  MetaAdsFormScope,
  MetaAdsIntegrationInput,
  MetaAdsIntegrationView,
  MetaFormFollowUpActionsInput,
} from "@/lib/meta-ads/types"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import {
  acknowledgeMetaAdsFormHandoffAction,
  beginMetaAdsOAuthAction,
  checkMetaAdsConnectionAction,
  completeMetaAdsOAuthAction,
  createMetaAdsFormCopyAction,
  disconnectMetaAdsIntegrationAction,
  sendMetaAdsTestLeadAction,
  saveMetaAdsFormFollowUpActionsAction,
} from "@/server-actions/meta-ads"
import { useTranslations } from "next-intl"


type FormErrorSetter = (errors: Record<string, unknown>) => void

export function useSaveMetaAdsFormFollowUpActionsMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsMetaAds")

  const cache = useMetaAdsCache()
  return useMutation({
    mutationFn: async (input: MetaFormFollowUpActionsInput) => {
      const result = await withQueryHandling(saveMetaAdsFormFollowUpActionsAction(input), {
        loading: tUi("savingInstantFormFollowUp"), success: tUi("followUpActionsSaved"),
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: cache.set,
  })
}

export function useBeginMetaAdsOAuthMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsMetaAds")

  return useMutation({
    mutationFn: async (input: MetaAdsIntegrationInput & { setErrors?: FormErrorSetter }) => {
      const { setErrors, ...values } = input
      const result = await withQueryHandling(beginMetaAdsOAuthAction(values), {
        loading: tUi("preparingMetaAuthorization"),
        showErrorToast: false,
        setErrors,
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function useAcknowledgeMetaAdsFormHandoffMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsMetaAds")

  const cache = useMetaAdsCache()
  return useMutation({
    mutationFn: async (copiedFormId: string) => {
      const result = await withQueryHandling(acknowledgeMetaAdsFormHandoffAction({
        copiedFormId,
      }), {
        loading: tUi("reCheckingTheCopiedInstantForm"),
        success: tUi("liveAdUpdateConfirmationRecorded"),
        showErrorToast: false,
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: cache.set,
  })
}

export function useCompleteMetaAdsOAuthMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsMetaAds")

  const cache = useMetaAdsCache()
  return useMutation({
    mutationFn: async (input: {
      sessionId: string
      pageId: string
      adAccountId: string
      phoneFieldKey: string
      formScope: MetaAdsFormScope
      selectedFormIds: string[]
    }) => {
      const result = await withQueryHandling(completeMetaAdsOAuthAction(input), {
        loading: tUi("connectingTheSelectedFacebookPage"),
        success: tUi("metaAdsConnected"),
        showErrorToast: false,
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: result => cache.set(result.integration),
  })
}

export function useCreateMetaAdsFormCopyMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsMetaAds")

  const cache = useMetaAdsCache()
  return useMutation({
    mutationFn: async (input: { sourceFormId: string; name: string }) => {
      const result = await withQueryHandling(createMetaAdsFormCopyAction(input), {
        loading: tUi("creatingAVersafyReadyInstantForm"),
        success: tUi("unrestrictedInstantFormCreated"),
        showErrorToast: false,
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: result => cache.set(result.integration),
  })
}

export function useCheckMetaAdsMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsMetaAds")

  const cache = useMetaAdsCache()
  return useMutation({
    mutationFn: async () => {
      const result = await withQueryHandling(checkMetaAdsConnectionAction({}), {
        loading: tUi("checkingMetaAdsConnection"),
        success: value => value.status === "connected"
          ? "Meta Ads is connected"
          : "Meta Ads setup still needs attention",
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: cache.set,
  })
}

export function useSendMetaAdsTestLeadMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsMetaAds")

  const cache = useMetaAdsCache()
  return useMutation({
    mutationFn: async (input: { phoneNumber: string; setErrors?: FormErrorSetter }) => {
      const { setErrors, ...values } = input
      const result = await withQueryHandling(sendMetaAdsTestLeadAction(values), {
        loading: tUi("sendingAMetaTestLead"),
        success: tUi("testLeadSentWaitingForItsCallbackTask"),
        showErrorToast: false,
        setErrors,
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: cache.set,
  })
}

export function useDisconnectMetaAdsMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsMetaAds")

  const cache = useMetaAdsCache()
  return useMutation({
    mutationFn: async () => {
      const result = await withQueryHandling(disconnectMetaAdsIntegrationAction({}), {
        loading: tUi("disconnectingMetaAdsAndDeletingItsData"),
        success: tUi("metaAdsDisconnectedAndItsDataDeleted"),
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: () => cache.invalidate(),
  })
}

function useMetaAdsCache() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const queryKey = metaAdsIntegrationQueryKey(getQueryTenantId(user))
  return {
    set: (value: MetaAdsIntegrationView) => queryClient.setQueryData(queryKey, value),
    invalidate: () => queryClient.invalidateQueries({ queryKey }),
  }
}
